import {
  ADD_FAVORITE,
  BULK_ADD_FAVORITE,
  BULK_DELETE_FAVORITE,
  DELETE_FAVORITE,
  EXISTS_FAVORITE,
  FavoritesListResponse,
  GET_CONTENT_HUB_DATA,
  GET_FAVORITES,
  GET_FOOTER,
  GET_HEADER,
  GET_MY_ACCOUNT_DATA,
  GET_SEARCH,
  GET_SITE_CONFIGURATION,
  GetContentHubDataResponse,
  GetMyAccountDataResponse,
  REFRESH_TOKEN,
  RefreshTokenResponse,
  VALIDATE_TOKEN,
  ValidateTokenResponse,
} from '../graphql/queries/posts';
import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  useQuery,
} from '@apollo/client';

import { ISearchQuery } from '../system-selector/interfaces';

let process: NodeJS.Process | undefined =
  typeof window === 'undefined' ? global.process : window.process;

// Configuration IDs for different environments
// These IDs are used to fetch specific configurations from the API.
const HEADLESS_CONFIGURATION_ID =
  process && process.env.NEXT_PUBLIC_HEADLESS_CONFIGURATION_ID?.trim() !== ''
    ? process.env.NEXT_PUBLIC_HEADLESS_CONFIGURATION_ID
    : '2519805';

export const getApiBaseUrl = (): string => {
  return process &&
    process.env.NEXT_PUBLIC_GRAPHQL_API_ENDPOINT_URL?.trim() !== ''
    ? process.env.NEXT_PUBLIC_GRAPHQL_API_ENDPOINT_URL!
    : 'https://siniat-buat.etexwebplatform.com';
};

export const makeClient = () => {
  const API_BASE_URL = getApiBaseUrl();

  console.log('API changed to:', API_BASE_URL);

  const httpLink = createHttpLink({
    uri: `${API_BASE_URL}/graphql`,
  });

  return new ApolloClient({
    cache: new InMemoryCache(),
    link: httpLink,
  });
};

// Option 1: Using a function that takes the ApolloClient instance
export const fetchContentHubData = async (
  filter: string,
  page: number,
  pageSize: number
): Promise<GetContentHubDataResponse | undefined> => {
  try {
    const { data } = await makeClient().query<GetContentHubDataResponse>({
      query: GET_CONTENT_HUB_DATA,
      variables: { filter, page, pageSize },
    });
    return data;
  } catch (error) {
    console.error('Error fetching ContentHub data:', error);
    return undefined;
  }
};

export const fetchHeaderData = async (
  opts?: { url?: string; simplifiedHeader?: boolean; language?: string }
): Promise<any | undefined> => {
  try {
    const accessToken =
      typeof window !== 'undefined'
        ? sessionStorage.getItem('accessToken')
        : null;

    const derivedUrl =
      opts?.url ??
      (typeof window !== 'undefined' && window.location?.href
        ? window.location.href
        : undefined);

    const variables = {
      url: derivedUrl ?? null,
      simplifiedHeader:
        typeof opts?.simplifiedHeader === 'boolean' ? opts!.simplifiedHeader : null,
      language: opts?.language ?? 'en-gb',
    } as const;

    const { data } = await makeClient().query<any>({
      query: GET_HEADER,
      variables,
      context: {
        headers: {
          ...(accessToken && {
            Authorization: `Bearer ${accessToken}`,
          }),
        },
      },
    });
    return data;
  } catch (error) {
    console.error('Error fetching Header data:', error);
    return undefined;
  }
};

export const fetchFooterData = async (
  opts?: { url?: string; simplifiedFooter?: boolean; language?: string }
): Promise<any | undefined> => {
  try {
    const accessToken =
      typeof window !== 'undefined'
        ? sessionStorage.getItem('accessToken')
        : null;

    const derivedUrl =
      opts?.url ??
      (typeof window !== 'undefined' && window.location?.href
        ? window.location.href
        : undefined);

    const variables = {
      url: derivedUrl ?? null,
      simplifiedFooter:
        typeof opts?.simplifiedFooter === 'boolean' ? opts!.simplifiedFooter : null,
      language: opts?.language ?? 'en-gb',
    } as const;

    const { data } = await makeClient().query<any>({
      query: GET_FOOTER,
      variables,
      context: {
        headers: {
          ...(accessToken && {
            Authorization: `Bearer ${accessToken}`,
          }),
        },
      },
    });
    return data;
  } catch (error) {
    console.error('Error fetching Footer data:', error);
    return undefined;
  }
};

// Option 2: Using a custom hook (more React-centric)
export const useGetContentHubData = (
  filter: string,
  page: number,
  pageSize: number
) => {
  const accessToken =
    typeof window !== 'undefined'
      ? sessionStorage.getItem('accessToken')
      : null;
  if (accessToken) {
    return useQuery<GetContentHubDataResponse>(GET_CONTENT_HUB_DATA, {
      variables: { filter, page, pageSize },
      skip: !page, // Skip the query if page is not provided
      // Include the access token in the context for authentication
      context: {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    });
  } else {
    return useQuery<GetContentHubDataResponse>(GET_CONTENT_HUB_DATA, {
      variables: { filter, page, pageSize },
      skip: !page, // Skip the query if page is not provided
    });
  }
};

export const refreshToken = async (): Promise<
  RefreshTokenResponse | undefined
> => {
  try {
    const accessToken =
      typeof window !== 'undefined'
        ? sessionStorage.getItem('accessToken')
        : null;
    const { data } = await makeClient().query<RefreshTokenResponse>({
      query: REFRESH_TOKEN,
      ...(accessToken && {
        context: {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      }),
    });
    return data;
  } catch (error) {
    return undefined;
  }
};

export const validateToken = async (
  token: string
): Promise<ValidateTokenResponse | undefined> => {
  try {
    const accessToken =
      typeof window !== 'undefined'
        ? sessionStorage.getItem('accessToken')
        : null;
    const { data } = await makeClient().query<ValidateTokenResponse>({
      query: VALIDATE_TOKEN,
      variables: { token },
      ...(accessToken && {
        context: {
          // anonymous access
        },
      }),
    });
    return data;
  } catch (error) {
    console.error('Error fetching VALIDATE_TOKEN data:', error);
    return undefined;
  }
};

export const getFavorites = async (): Promise<
  FavoritesListResponse | undefined
> => {
  try {
    const accessToken =
      typeof window !== 'undefined'
        ? sessionStorage.getItem('accessToken')
        : null;
    const { data } = await makeClient().query<FavoritesListResponse>({
      query: GET_FAVORITES,
      ...(accessToken && {
        context: {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      }),
    });
    return data;
  } catch (error) {
    console.error('Error fetching GET_FAVORITES data:', error);
    return undefined;
  }
};

export const deleteFavorite = async (id: string): Promise<boolean> => {
  try {
    const accessToken =
      typeof window !== 'undefined'
        ? sessionStorage.getItem('accessToken')
        : null;
    const { data } = await makeClient().query<boolean>({
      query: DELETE_FAVORITE,
      variables: { id },
      ...(accessToken && {
        context: {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      }),
    });
    return data;
  } catch (error) {
    console.error('Error fetching DELETE_FAVORITE data:', error);
    return false;
  }
};

export const addFavorite = async (id: string): Promise<boolean> => {
  try {
    const accessToken =
      typeof window !== 'undefined'
        ? sessionStorage.getItem('accessToken')
        : null;
    const { data } = await makeClient().query<boolean>({
      query: ADD_FAVORITE,
      variables: { id },
      ...(accessToken && {
        context: {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      }),
    });
    return data;
  } catch (error) {
    console.error('Error fetching ADD_FAVORITE data:', error);
    return false;
  }
};

export const existsFavorite = async (id: string): Promise<boolean> => {
  try {
    const accessToken =
      typeof window !== 'undefined'
        ? sessionStorage.getItem('accessToken')
        : null;
    const { data } = await makeClient().query<boolean>({
      query: EXISTS_FAVORITE,
      variables: { id },
      ...(accessToken && {
        context: {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      }),
    });
    return data;
  } catch (error) {
    console.error('Error fetching EXISTS_FAVORITE data:', error);
    return false;
  }
};

export const BulkAddFavorites = async (id: string[]): Promise<boolean> => {
  try {
    const accessToken =
      typeof window !== 'undefined'
        ? sessionStorage.getItem('accessToken')
        : null;
    const { data } = await makeClient().query<boolean>({
      query: BULK_ADD_FAVORITE,
      variables: { id },
      ...(accessToken && {
        context: {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      }),
    });
    return data;
  } catch (error) {
    console.error('Error fetching BULK_ADD_FAVORITE data:', error);
    return false;
  }
};

export const BulkDeleteFavorites = async (id: string[]): Promise<boolean> => {
  try {
    const accessToken =
      typeof window !== 'undefined'
        ? sessionStorage.getItem('accessToken')
        : null;
    const { data } = await makeClient().query<boolean>({
      query: BULK_DELETE_FAVORITE,
      variables: { id },
      ...(accessToken && {
        context: {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      }),
    });
    return data;
  } catch (error) {
    console.error('Error fetching BULK_DELETE_FAVORITE data:', error);
    return false;
  }
};

export const fetchMyAccountInfo = async (): Promise<
  GetMyAccountDataResponse | undefined
> => {
  try {
    const accessToken =
      typeof window !== 'undefined'
        ? sessionStorage.getItem('accessToken')
        : null;
    const { data } = await makeClient().query<GetMyAccountDataResponse>({
      query: GET_MY_ACCOUNT_DATA,
      ...(accessToken && {
        context: {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      }),
    });
    return data;
  } catch (error) {
    console.error('Error fetching My Account data:', error);
    return undefined;
  }
};

export const fetchSiteConfiguration = async (): Promise<any | undefined> => {
  try {
    const { data } = await makeClient().query<any>({
      fetchPolicy: 'network-only',
      query: GET_SITE_CONFIGURATION,
      variables: { language: 'en' },
      context: {
        headers: {
          'X-Etex-ConfigToken': HEADLESS_CONFIGURATION_ID,
        },
      },
    });
    return data;
  } catch (error) {
    console.error('Error fetching SiteConfiguration data:', error);
    return undefined;
  }
};

export const fetchSearchData = async (
  search: ISearchQuery
): Promise<any | undefined> => {
  try {
    const accessToken =
      typeof window !== 'undefined'
        ? sessionStorage.getItem('accessToken')
        : null;
    if (accessToken) {
      const { data } = await makeClient().query<any>({
        query: GET_SEARCH,
        variables: { search },
        context: {
          headers: {
            'X-Etex-ConfigToken': HEADLESS_CONFIGURATION_ID,
            'Accept-Language': 'en-US,en;q=0.9',
            Authorization: `Bearer ${accessToken}`,
          },
        },
      });
      return data;
    } else {
      const { data } = await makeClient().query<any>({
        query: GET_SEARCH,
        variables: { search },
        context: {
          headers: {
            'X-Etex-ConfigToken': HEADLESS_CONFIGURATION_ID,
            'Accept-Language': 'en-US,en;q=0.9',
          },
        },
      });
      return data;
    }
  } catch (error) {
    console.error('Error fetching Search data:', error);
    return undefined;
  }
};

export const redirectToComparePage = (entityIds: string[]) => {
  const baseUrl =
    `${getApiBaseUrl()}/en-gb/products-and-systems/systems/system-compare-page/`;
  const url = `${baseUrl}?entityIds=${entityIds.join(',')}`;
  window.location.href = url;
};

export const redirectToSystemDetails = (name: string) => {
  const baseUrl =
    `${getApiBaseUrl()}/en-gb/products-and-systems/systems/partition-systems`;
  window.location.href = `${baseUrl}/${name}`;
};

export const exportToExcel = async (): Promise<{ statusUrl: string }> => {
  try {
    const baseUrl = getApiBaseUrl();
    const accessToken =
      typeof window !== 'undefined'
        ? sessionStorage.getItem('accessToken')
        : null;

    const response = await fetch(
      `${baseUrl}/-/v0/ExportToExcelButton/Export?epslanguage=en-GB`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
        },
        body: JSON.stringify({
          filter: '',
          category: {
            id: 68613,
            workID: 0,
            providerName: null,
            getPublishedOrLatest: false,
            isExternalProvider: false,
            isReadOnly: true,
          },
          sortAttribute: '',
          sortDirection: '',
          entityTypeKey: 'system',
          prefilterConfig: {
            id: 255963,
            workID: 0,
            providerName: null,
            getPublishedOrLatest: false,
            isExternalProvider: false,
            isReadOnly: true,
          },
        }),
      }
    );

    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

    const html = await response.text();
    const scriptMatch = html.match(/<script type="json">([\s\S]*?)<\/script>/);

    if (!scriptMatch) {
      throw new Error('Job JSON not found in export response.');
    }

    const jobData = JSON.parse(scriptMatch[1]);
    return {
      statusUrl: `${baseUrl}${jobData.externalJobStatusUrl}&epslanguage=en-GB`,
    };
  } catch (error) {
    console.error('Export to Excel failed:', error);
    throw error;
  }
};

export const pollJobStatus = async (
  statusUrl: string,
  intervalMs = 3000,
  timeoutMs = 60000
): Promise<{ deliveryUrl: string; fileName: string }> => {
  const start = Date.now();

  while (true) {
    if (Date.now() - start > timeoutMs) {
      throw new Error('Export polling timed out.');
    }

    const res = await fetch(statusUrl);
    if (!res.ok) throw new Error(`Status fetch failed: ${res.status}`);
    const html = await res.text();
    const scriptMatch = html.match(/<script type="json">([\s\S]*?)<\/script>/);

    if (!scriptMatch) throw new Error('Job JSON not found in status response.');
    const jobData = JSON.parse(scriptMatch[1]);

    if (jobData.failed) throw new Error('Export job failed.');
    if (jobData.ready && jobData.deliveryUrl) {
      return {
        deliveryUrl: jobData.deliveryUrl,
        fileName: jobData.fileName || 'download.xlsx',
      };
    }

    await new Promise((r) => setTimeout(r, intervalMs));
  }
};
